import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { formatISO } from 'date-fns';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import {
  updateBulkInvoiceStatus,
  commonPayInvoice,
  previewCommonPay
} from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';

const PaymentForm = ({
  isOpen,
  onClose,
  invoiceData,
  dueAmount,
  commonPay,
  statusId,
  chequeData,
  onSuccess
}) => {
  // Payment modal state
  const [payments, setPayments] = useState([
    {
      amount_paid: '',
      payment_date: '',
      payment_mode: '',
      transaction_id: '',
      type: 'Credited',
      selected_cheque_id: null,
      remarks: ''
    }
  ]);

  // Cheque management state
  const [availableCheques, setAvailableCheques] = useState([]);
  const [selectedCheques, setSelectedCheques] = useState({});

  // Payment mode options (legacy `nft` rows display as NEFT via normalizePaymentModeDisplay)
  const paymentModeOptions = [
    { value: 'cheque', label: 'Cheque' },
    { value: 'upi', label: 'UPI' },
    { value: 'internet_banking', label: 'Internet Banking' },
    { value: 'rtgs', label: 'RTGS' },
    { value: 'neft', label: 'NEFT' },
    { value: 'cash', label: 'Cash' }
  ];
  const [parkExcessConfirmed, setParkExcessConfirmed] = useState(false);
  const [dealPayPreview, setDealPayPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Payment type options - filter based on deal status for common pay
  const getPaymentTypeOptions = () => {
    const baseOptions = [
      { value: 'Credited', label: 'Credited' },
      { value: 'Debited', label: 'Debited' }
    ];

    // For common pay, only allow "Debited" if deal status is "Cancelled"
    if (commonPay && invoiceData?.data?.[0]?.closedDeal?.status !== 'Cancelled') {
      return baseOptions.filter((option) => option.value === 'Credited');
    }

    return baseOptions;
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue
  } = useForm();

  // Initialize available cheques when component mounts or chequeData changes
  useEffect(() => {
    if (chequeData) {
      setAvailableCheques(chequeData);
    }
  }, [chequeData]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Only set available cheques if they haven't been set yet
      if (availableCheques.length === 0) {
        setAvailableCheques(chequeData || []);
      }
      setSelectedCheques({});
      setPayments([
        {
          amount_paid: '',
          payment_date: '',
          payment_mode: '',
          transaction_id: '',
          type: 'Credited',
          selected_cheque_id: null,
          remarks: ''
        }
      ]);
      setParkExcessConfirmed(false);
      setDealPayPreview(null);
    }
  }, [isOpen, chequeData]);

  const sanitizeToNumber = (val) => {
    if (val === null || val === undefined) return NaN;
    const s = String(val)
      .replace(/[^0-9.-]/g, '')
      .trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };

  const refreshDealPayPreview = async () => {
    if (!commonPay || !statusId) return;
    const total = payments.reduce((s, p) => {
      const amt = sanitizeToNumber(p.amount_paid);
      return s + (Number.isFinite(amt) ? amt : 0);
    }, 0);
    if (!(total > 0)) {
      setDealPayPreview(null);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await previewCommonPay(statusId, { amount: Math.round(total) });
      setDealPayPreview(res?.data || res);
    } catch (err) {
      setDealPayPreview(null);
      toast.error(
        err?.response?.data?.message || err?.message || 'Could not preview allocation'
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  // Helper function to get transaction ID label based on payment mode
  const getTransactionIdLabel = (paymentMode) => {
    switch (paymentMode) {
      case 'cheque':
        return 'Check Number';
      case 'cash':
        return null; // No field for cash
      default:
        return 'Transaction ID';
    }
  };

  // Helper function to handle cheque selection
  const handleChequeSelection = (paymentIndex, chequeId) => {
    const selectedCheque = availableCheques.find((cheque) => cheque.id === Number(chequeId));
    if (selectedCheque) {
      // Update selected cheques
      setSelectedCheques((prev) => ({
        ...prev,
        [paymentIndex]: selectedCheque
      }));

      // Update payment data with cheque details
      const newPayments = [...payments];
      newPayments[paymentIndex] = {
        ...newPayments[paymentIndex],
        amount_paid: selectedCheque.amount,
        payment_date: selectedCheque.date
          ? new Date(selectedCheque.date).toISOString().split('T')[0]
          : '',
        transaction_id: selectedCheque.cheque_number,
        selected_cheque_id: Number(chequeId),
        remarks: newPayments[paymentIndex].remarks || ''
      };
      setPayments(newPayments);

      // Don't remove the selected cheque from available cheques - keep it for display
      // setAvailableCheques((prev) => prev.filter((cheque) => cheque.id !== Number(chequeId)));
    }
  };

  // Helper function to handle cheque deselection
  const handleChequeDeselection = (paymentIndex) => {
    const selectedCheque = selectedCheques[paymentIndex];
    if (selectedCheque) {
      // Remove from selected cheques
      setSelectedCheques((prev) => {
        const newSelected = { ...prev };
        delete newSelected[paymentIndex];
        return newSelected;
      });

      // Reset payment data
      const newPayments = [...payments];
      newPayments[paymentIndex] = {
        amount_paid: '',
        payment_date: '',
        payment_mode: 'cheque',
        transaction_id: '',
        type: 'Credited',
        selected_cheque_id: null,
        remarks: newPayments[paymentIndex].remarks || ''
      };
      setPayments(newPayments);
    }
  };

  // Payment handling functions
  const handlePaymentChange = (index, field, value) => {
    const newPayments = [...payments];
    newPayments[index][field] = value;

    // If payment mode is changed, reset all payment fields for that row
    if (field === 'payment_mode') {
      newPayments[index] = {
        amount_paid: '',
        payment_date: '',
        payment_mode: value,
        transaction_id: '',
        type: 'Credited',
        selected_cheque_id: null,
        remarks: newPayments[index].remarks || ''
      };

      // If switching away from cheque, deselect any selected cheque
      if (payments[index].payment_mode === 'cheque' && payments[index].selected_cheque_id) {
        handleChequeDeselection(index);
      }
    }

    setPayments(newPayments);
  };

  const handleAddPaymentRow = () => {
    setPayments([
      ...payments,
      {
        amount_paid: '',
        payment_date: '',
        payment_mode: '',
        transaction_id: '',
        type: 'Credited',
        selected_cheque_id: null,
        remarks: ''
      }
    ]);
  };

  const handleRemovePaymentRow = (index) => {
    if (payments.length > 1) {
      // If this payment has a selected cheque, deselect it first
      const payment = payments[index];
      if (payment.payment_mode === 'cheque' && payment.selected_cheque_id) {
        handleChequeDeselection(index);
      }
      const newPayments = payments.filter((_, i) => i !== index);
      setPayments(newPayments);
    }
  };

  // Update all existing payment rows to have the type field when switching to common pay
  const updateExistingPaymentsType = () => {
    setPayments((prevPayments) =>
      prevPayments.map((payment) => ({
        ...payment,
        type: payment.type || 'Credited'
      }))
    );
  };

  // Dummy register function for FormInput components
  const dummyRegister = (fieldName) => ({
    onChange: () => {},
    onBlur: () => {},
    name: fieldName,
    ref: () => {}
  });

  // Reset payments on modal close
  const resetPaidModal = () => {
    setParkExcessConfirmed(false);
    setDealPayPreview(null);
    setPayments([
      {
        amount_paid: '',
        payment_date: '',
        payment_mode: '',
        transaction_id: '',
        type: 'Credited',
        selected_cheque_id: null,
        remarks: ''
      }
    ]);
    setSelectedCheques({});
    // Don't reset available cheques - keep them for next time
    reset();
    onClose();
  };

  const { mutate: handleUpdateStatus } = useMutation({
    mutationFn: async () => {
      // Validate all payments
      let totalEntered = 0;
      for (const p of payments) {
        if (!p.payment_mode) {
          toast.error('Please select payment mode for all payments.');
          const err = new Error('VALIDATION_MISSING_FIELDS');
          err.isValidation = true;
          return Promise.reject(err);
        }

        // For cheque payments, validate cheque selection
        if (p.payment_mode === 'cheque') {
          if (!p.selected_cheque_id) {
            toast.error('Please select a cheque for cheque payment.');
            const err = new Error('VALIDATION_MISSING_CHEQUE');
            err.isValidation = true;
            return Promise.reject(err);
          }
          // Amount and date are auto-filled from cheque, so use them
          const amt = sanitizeToNumber(p.amount_paid);
          if (!Number.isFinite(amt) || amt < 0) {
            toast.error('Invalid cheque amount.');
            const err = new Error('VALIDATION_INVALID_AMOUNT');
            err.isValidation = true;
            return Promise.reject(err);
          }
          totalEntered += amt;
        } else {
          // For non-cheque payments, validate amount, date, and transaction ID
          if (p.amount_paid === '' || !p.payment_date) {
            toast.error('Please fill amount and date for all non-cheque payments.');
            const err = new Error('VALIDATION_MISSING_FIELDS');
            err.isValidation = true;
            return Promise.reject(err);
          }

          const amt = sanitizeToNumber(p.amount_paid);
          if (!Number.isFinite(amt) || amt < 0) {
            toast.error('Please enter a valid amount (0 or greater).');
            const err = new Error('VALIDATION_INVALID_AMOUNT');
            err.isValidation = true;
            return Promise.reject(err);
          }
          totalEntered += amt;

          // Validate transaction ID for non-cash payments
          if (p.payment_mode !== 'cash' && !p.transaction_id) {
            const fieldName = p.payment_mode === 'cheque' ? 'check number' : 'transaction ID';
            toast.error(`Please fill ${fieldName} for ${p.payment_mode} payment.`);
            const err = new Error('VALIDATION_MISSING_TXN');
            err.isValidation = true;
            return Promise.reject(err);
          }
        }

        // Validate type field for common pay
        if (commonPay && !p.type) {
          toast.error('Please select type (Credited/Debited) for all payments.');
          const err = new Error('VALIDATION_MISSING_TYPE');
          err.isValidation = true;
          return Promise.reject(err);
        }

        // Validate that "Debited" type is only allowed for cancelled deals
        if (
          commonPay &&
          p.type === 'Debited' &&
          invoiceData?.data?.[0]?.closedDeal?.status !== 'Cancelled'
        ) {
          toast.error('Debited type is only allowed for cancelled deals.');
          const err = new Error('VALIDATION_INVALID_DEBITED_TYPE');
          err.isValidation = true;
          return Promise.reject(err);
        }
      }

      // Require a real amount. Over-invoice excess needs explicit park confirmation.
      if (!(totalEntered > 0)) {
        toast.error(
          commonPay
            ? 'Enter a payment amount. Pay applies to unpaid stage invoices first; leftover must be confirmed as parked cash credit.'
            : 'Please enter a payment amount greater than 0.'
        );
        const err = new Error('VALIDATION_ZERO_AMOUNT');
        err.isValidation = true;
        return Promise.reject(err);
      }

      const dueCap = Number(dueAmount || 0);
      if (!commonPay && dueCap > 0 && totalEntered > dueCap && !parkExcessConfirmed) {
        toast.error(
          `Amount exceeds invoice remaining due (₹${dueCap.toLocaleString('en-IN')}). Tick “Park excess as verified cash credit” to continue.`
        );
        const err = new Error('VALIDATION_OVERPAY_NEEDS_CONFIRM');
        err.isValidation = true;
        return Promise.reject(err);
      }

      if (commonPay) {
        let preview = dealPayPreview;
        try {
          const res = await previewCommonPay(statusId, {
            amount: Math.round(totalEntered)
          });
          preview = res?.data || res;
          setDealPayPreview(preview);
        } catch (previewErr) {
          toast.error(
            previewErr?.response?.data?.message ||
              previewErr?.message ||
              'Could not preview Deal Pay allocation'
          );
          const err = new Error('VALIDATION_PREVIEW_FAILED');
          err.isValidation = true;
          return Promise.reject(err);
        }
        const excess = Number(preview?.excess_to_park || 0);
        if (excess > 0 && !parkExcessConfirmed) {
          toast.error(
            `Deal Pay leaves ₹${excess.toLocaleString('en-IN')} unallocated. Review the preview and tick park excess to continue.`
          );
          const err = new Error('VALIDATION_OVERPAY_NEEDS_CONFIRM');
          err.isValidation = true;
          return Promise.reject(err);
        }
      }

      // Prepare payload
      const modeWrite = (m) => (m === 'nft' ? 'neft' : m);
      const payload = {
        payments: payments.map((p) => ({
          amount_paid: sanitizeToNumber(p.amount_paid),
          payment_date: formatISO(new Date(p.payment_date)),
          mode: modeWrite(p.payment_mode),
          transactionID: p.payment_mode === 'cash' ? null : p.transaction_id,
          type: p.type,
          ...(p.remarks && { remarks: p.remarks })
        })),
        park_excess_confirmed: parkExcessConfirmed
      };
      if (commonPay) {
        return commonPayInvoice(statusId, payload);
      } else {
        return updateBulkInvoiceStatus(statusId, payload);
      }
    },
    onSuccess: () => {
      toast.success(
        commonPay
          ? 'Payment recorded. Applied to unpaid invoices / credit — no new invoice is created.'
          : 'Invoice Is Updated'
      );
      resetPaidModal();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (err) => {
      if (
        err?.isValidation ||
        err?.message === 'VALIDATION_MISSING_FIELDS' ||
        err?.message === 'VALIDATION_INVALID_AMOUNT' ||
        err?.message === 'VALIDATION_MISSING_TXN' ||
        err?.message === 'VALIDATION_MISSING_TYPE' ||
        err?.message === 'VALIDATION_MISSING_CHEQUE' ||
        err?.message === 'VALIDATION_INVALID_DEBITED_TYPE' ||
        err?.message === 'VALIDATION_ZERO_AMOUNT' ||
        err?.message === 'VALIDATION_OVERPAY_NEEDS_CONFIRM' ||
        err?.message === 'VALIDATION_PREVIEW_FAILED'
      )
        return;
      toast.error(err?.response?.data?.message || err?.message || 'Error to update invoice');
    }
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[55%] max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Payment</h2>
            <button
              onClick={resetPaidModal}
              className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex justify-center flex-col mb-8">
            {invoiceData.data[0]?.closedDeal?.status === 'Cancelled' && (
              <p>
                Final Payable -{' '}
                {`₹${new Intl.NumberFormat('en-IN').format(
                  invoiceData.data[0]?.closedDeal?.finalPayable || 0
                )}`}
              </p>
            )}
            {invoiceData.data[0]?.closedDeal?.status !== 'Cancelled' && (
              <p>
                Pending Invoice Amount - {`₹${new Intl.NumberFormat('en-IN').format(dueAmount)}`}
              </p>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdateStatus();
              }}
              className="flex flex-col gap-4 pt-6">
              {/* Multiple Payments List */}
              {payments.map((payment, idx) => (
                <div
                  key={idx}
                  className="flex flex-col gap-4 w-full border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Payment {idx + 1}</h4>
                    <Button
                      type="button"
                      displayType="secondary"
                      onClick={() => {
                        // If this payment has a selected cheque, deselect it first
                        if (payment.payment_mode === 'cheque' && payment.selected_cheque_id) {
                          handleChequeDeselection(idx);
                        }
                        handleRemovePaymentRow(idx);
                      }}
                      disabled={payments.length === 1}>
                      Remove
                    </Button>
                  </div>

                  {/* Payment Mode Selection - Always First */}
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Mode <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={payment.payment_mode}
                      onChange={(e) => {
                        const newMode = e.target.value;
                        handlePaymentChange(idx, 'payment_mode', newMode);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required>
                      <option value="">Select Payment Mode</option>
                      {paymentModeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Conditional Fields Based on Payment Mode */}
                  {payment.payment_mode && (
                    <div className="flex flex-col gap-4">
                      {/* For Cheque Mode - Show Cheque Dropdown */}
                      {payment.payment_mode === 'cheque' && (
                        <div className="w-full">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Cheque <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={payment.selected_cheque_id || ''}
                            onChange={(e) => {
                              const chequeId = e.target.value;
                              if (chequeId) {
                                handleChequeSelection(idx, parseInt(chequeId));
                              } else {
                                handleChequeDeselection(idx);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required>
                            <option value="">Select a Cheque</option>
                            {availableCheques
                              ?.filter((cheque) => {
                                // Filter out cheques that are already selected in other payment rows
                                const isSelectedInOtherRows = Object.values(selectedCheques).some(
                                  (selectedCheque) => selectedCheque.id === cheque.id
                                );
                                // Allow the current payment's selected cheque to remain in the list
                                const isCurrentPaymentCheque =
                                  payment.selected_cheque_id === cheque.id;
                                return !isSelectedInOtherRows || isCurrentPaymentCheque;
                              })
                              .map((cheque) => (
                                <option key={cheque.id} value={cheque.id}>
                                  {cheque.cheque_number} - ₹
                                  {new Intl.NumberFormat('en-IN').format(cheque.amount)} -{' '}
                                  {cheque.account_holder_name}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      {/* For Non-Cheque Modes - Show Amount and Date Fields */}
                      {payment.payment_mode !== 'cheque' && (
                        <div className="flex gap-4 items-end w-full">
                          <FormInput
                            width={49}
                            label={'How much are you paying'}
                            isRequired
                            register_key={`payingAmount_${idx}`}
                            type={'number'}
                            valuePre={payment.amount_paid}
                            onType={(val) =>
                              handlePaymentChange(
                                idx,
                                'amount_paid',
                                val.target ? val.target.value : val
                              )
                            }
                            register={dummyRegister}
                          />
                          <FormInput
                            width={49}
                            label={'Payment Date'}
                            isRequired
                            register_key={`paymentDate_${idx}`}
                            type={'date'}
                            valuePre={payment.payment_date}
                            CustomFeature={{
                              max: new Date().toISOString().split('T')[0]
                            }}
                            onType={(val) =>
                              handlePaymentChange(
                                idx,
                                'payment_date',
                                val.target ? val.target.value : val
                              )
                            }
                            register={dummyRegister}
                          />
                        </div>
                      )}

                      {/* Transaction ID and Type in one row */}
                      {(payment.payment_mode &&
                        payment.payment_mode !== 'cash' &&
                        payment.payment_mode !== 'cheque') ||
                      commonPay ? (
                        <div className="flex gap-4 items-end w-full">
                          {/* Transaction ID Field - For Non-Cash, Non-Cheque Payments */}
                          {payment.payment_mode &&
                            payment.payment_mode !== 'cash' &&
                            payment.payment_mode !== 'cheque' && (
                              <FormInput
                                width={commonPay ? 49 : 100}
                                label={getTransactionIdLabel(payment.payment_mode)}
                                isRequired
                                register_key={`transactionId_${idx}`}
                                type={'text'}
                                valuePre={payment.transaction_id}
                                onType={(val) =>
                                  handlePaymentChange(
                                    idx,
                                    'transaction_id',
                                    val.target ? val.target.value : val
                                  )
                                }
                                register={dummyRegister}
                              />
                            )}

                          {/* Payment Type Dropdown - Only show for common pay */}
                          {commonPay && (
                            <div className="w-[49%]">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type <span className="text-red-500">*</span>
                              </label>

                              {invoiceData?.data?.[0]?.closedDeal?.status !== 'Cancelled' && (
                                <p className="text-xs text-gray-500 mb-2">
                                  Only &quot;Credited&quot; type is available for active deals
                                </p>
                              )}
                              <select
                                value={payment.type}
                                onChange={(e) => handlePaymentChange(idx, 'type', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required>
                                {getPaymentTypeOptions().map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      ) : null}

                      {/* Remarks Field - Optional for all payment modes */}
                      <div className="w-full">
                        <FormInput
                          width={100}
                          label={'Remarks'}
                          register_key={`remarks_${idx}`}
                          type={'text'}
                          placeholder={'Add any additional notes (optional)'}
                          valuePre={payment.remarks}
                          onType={(val) =>
                            handlePaymentChange(idx, 'remarks', val.target ? val.target.value : val)
                          }
                          register={dummyRegister}
                        />
                      </div>

                      {/* Show Selected Cheque Details */}
                      {payment.payment_mode === 'cheque' &&
                        payment.selected_cheque_id &&
                        selectedCheques[idx] && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <h5 className="text-sm font-medium text-blue-800 mb-2">
                              Selected Cheque Details:
                            </h5>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-600">Cheque Number:</span>
                                <span className="ml-2 font-medium">
                                  {selectedCheques[idx].cheque_number}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Amount:</span>
                                <span className="ml-2 font-medium">
                                  ₹
                                  {new Intl.NumberFormat('en-IN').format(
                                    selectedCheques[idx].amount
                                  )}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Date:</span>
                                <span className="ml-2 font-medium">
                                  {selectedCheques[idx].date
                                    ? new Date(selectedCheques[idx].date).toLocaleDateString(
                                        'en-IN'
                                      )
                                    : 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Account Holder:</span>
                                <span className="ml-2 font-medium">
                                  {selectedCheques[idx].account_holder_name}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              ))}
              <div>
                <Button type="button" onClick={handleAddPaymentRow} displayType="secondary">
                  + Add Payment
                </Button>
              </div>

              {commonPay && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="font-medium text-gray-800">Deal Pay allocation preview</span>
                    <Button
                      type="button"
                      displayType="secondary"
                      onClick={refreshDealPayPreview}
                      disabled={previewLoading}>
                      {previewLoading ? 'Loading…' : 'Refresh preview'}
                    </Button>
                  </div>
                  {!dealPayPreview ? (
                    <p className="text-xs text-gray-500">
                      Enter amounts, then refresh preview before saving. Waterfall applies to
                      unlocked unpaid heads up to remaining due.
                    </p>
                  ) : (
                    <ul className="space-y-1 text-xs">
                      {(dealPayPreview.allocations || []).map((line) => (
                        <li key={`${line.invoice_id}-${line.source}`}>
                          {line.source || `Invoice #${line.invoice_id}`}: allocate{' '}
                          ₹{Number(line.allocate || 0).toLocaleString('en-IN')} of remaining due ₹
                          {Number(line.remaining_due || 0).toLocaleString('en-IN')}
                        </li>
                      ))}
                      {Number(dealPayPreview.excess_to_park || 0) > 0 && (
                        <li className="text-amber-800 font-medium">
                          Excess to park as verified cash credit: ₹
                          {Number(dealPayPreview.excess_to_park).toLocaleString('en-IN')}
                        </li>
                      )}
                      {!(dealPayPreview.allocations || []).length &&
                        !(Number(dealPayPreview.excess_to_park || 0) > 0) && (
                          <li>No unpaid invoice heads to allocate.</li>
                        )}
                    </ul>
                  )}
                </div>
              )}

              {!commonPay && Number(dueAmount || 0) > 0 && (
                <p className="text-xs text-gray-600">
                  Remaining due on this head:{' '}
                  ₹{Number(dueAmount || 0).toLocaleString('en-IN')}
                </p>
              )}

              <label className="flex items-start gap-2 text-sm text-gray-700 mt-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={parkExcessConfirmed}
                  onChange={(e) => setParkExcessConfirmed(e.target.checked)}
                />
                <span>
                  If amount exceeds invoice / stage remaining due, park the excess as{' '}
                  <strong>verified cash credit</strong> (linked to this receipt). Required when
                  overpaying a head.
                </span>
              </label>

              <div className="w-full flex justify-end items-center space-x-4 mt-8">
                <Button type="button" onClick={resetPaidModal} displayType="secondary">
                  Cancel
                </Button>
                <Button type="submit">Add</Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
